/**
 * Audio (docs/spec/05): SID-style effects and music synthesised with WebAudio, and any music
 * files dropped into `/music`. The game talks to this interface only: it feeds in each scan's
 * events and the continuous state, and nothing here reaches back into the simulation.
 *
 *   const audio = createAudio();                       // safe before any gesture, and without WebAudio
 *   addEventListener('keydown', () => audio.unlock());  // and pointerdown, touchstart
 *   audio.music('title');
 *   // each scan: audio.events(state.events, viewCentreX); audio.ambience({ amoeba, magic, cover });
 */

import { audioContextClass } from './bus';
import { musicFileNames } from './fileMusic';
import { LiveAudio } from './live';
import type { GameAudio } from './types';

export type { Ambience, Cue, GameAudio, MusicTrack, MusicVariant, UiSound, Volumes } from './types';
export { DEFAULT_VOLUMES } from './types';

/** Where there is no WebAudio: every method does nothing, except say which music files exist. */
class SilentAudio implements GameAudio {
  unlock(): void {}
  events(): void {}
  ambience(): void {}
  ui(): void {}
  cue(): void {}
  music(): void {}
  setVolumes(): void {}
  tracks(): string[] {
    return musicFileNames();
  }
}

/** Never throws: without an AudioContext (Node, a locked-down webview) the game simply runs silent. */
export function createAudio(): GameAudio {
  const Ctor = audioContextClass();
  if (!Ctor) return new SilentAudio();
  try {
    return new LiveAudio(Ctor);
  } catch {
    return new SilentAudio();
  }
}
